import React from 'react';
import { useAppDispatch } from 'ducks/hooks';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { getSelectedGroupId } from 'ducks/products';
import Sheet from 'ui/Sheet/Sheet';
import { isCornerWallInfoSheetVisible, setCornerWallInfoSheetVisible } from '../AutofillPax';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import {
  KompisSheetBody,
  KompisSheetBodyPadding,
  KompisSheetFooter,
  KompisSheetFooterButtons,
  KompisSheetHeader
} from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import HingedDoorsImage from '../images/AutofillCornerHingedDoors.png';
import { MeasurementUtility } from '@inter-ikea-kompis/utilities';
import { useTranslations } from 'hooks/useTranslations';
import Body from 'ui/Typography/Body';
import { useBehaviorTracker } from 'contexts/behaviors';
import { useServiceSettings } from 'contexts/serviceSettings';

const TitleWrapper = styled.div`
  width: 100%;
  padding-bottom: 2rem;
  position: fixed;
  top: 5rem;
  background: ${({ theme }) => theme.colors.white};
`;

const Image = styled.img`
  max-width: 300px;
`;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem;
`;

const Subtitle = styled.h3`
  padding: 1rem 0;
`;

const AutoFillCornerWallInfoSheet = () => {
  const dispatch = useAppDispatch();
  const sheetVisible = useSelector(isCornerWallInfoSheetVisible);
  const { formatMessage } = useIntl();
  const selectedGroupId = useSelector(getSelectedGroupId);
  const { useMetric } = useTranslations();
  const { translations } = useServiceSettings();
  const behaviorTracker = useBehaviorTracker();

  const onClose = () => {
    behaviorTracker
      .withContext({
        groupId: selectedGroupId,
        menuType: 'sheet',
        widgetId: 'autofill-corner-wall-door-type-info-sheet'
      })
      .custom(() => {
        dispatch(setCornerWallInfoSheetVisible(false));
      });
  };

  const unit = formatMessage({ id: useMetric ? 'storageone.cm' : 'storageone.inches' });

  return sheetVisible ? (
    <Sheet visible={sheetVisible} onOverlayClick={onClose}>
      <KompisSheetHeader theme={SkapaTheme} onCloseButtonClick={onClose} />
      <KompisSheetBody theme={SkapaTheme}>
        <KompisSheetBodyPadding theme={SkapaTheme}>
          <TitleWrapper>
            <Title type={ThemeFontStyleTypeEnum.headingMedium} style={{ marginTop: 0 }}>
              {formatMessage({ id: 'storageone.autofillMenuDoorTypeTitle' })}
            </Title>
          </TitleWrapper>
          <Body type={ThemeFontStyleTypeEnum.bodyMedium} mt="3rem">
            {formatMessage({ id: 'storageone.autofillPaxInfoSheetPreamble' })}
          </Body>
          <Subtitle>
            {formatMessage({ id: 'storageone.autofillPaxCornerWallInfoSheetHingedDoorTitle' })}
          </Subtitle>
          <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
            {formatMessage({ id: 'storageone.autofillPaxCornerWallInfoSheetHingedDoorText1' })}
          </Body>
          <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
            {formatMessage(
              { id: 'storageone.autofillPaxCornerWallInfoSheetHingedDoorText2' },
              {
                width100: useMetric
                  ? `100 ${unit}`
                  : `${MeasurementUtility.metricToImperial(translations.kompis, 1000)}`,
                width35: useMetric
                  ? `35 ${unit}`
                  : `${MeasurementUtility.metricToImperial(translations.kompis, 350)}`
              }
            )}
          </Body>
          <ImageWrapper>
            <Image
              src={HingedDoorsImage}
              alt={formatMessage({
                id: 'storageone.autofillPaxInfoSheetHingedDoorImageAlt'
              })}
            />
          </ImageWrapper>
          <Subtitle>
            {formatMessage({ id: 'storageone.autofillPaxInfoSheetSlidingDoorsTitle' })}
          </Subtitle>
          <Body type={ThemeFontStyleTypeEnum.bodyMedium} style={{ marginBottom: '1rem' }}>
            {formatMessage({ id: 'storageone.autofillPaxCornerWallInfoSheetSlidingDoorText' })}
          </Body>
        </KompisSheetBodyPadding>
      </KompisSheetBody>
      <KompisSheetFooter theme={SkapaTheme}>
        <KompisSheetFooterButtons
          compact={false}
          data-testid="question-sheet-footer-buttons"
          theme={SkapaTheme}
          primaryLabel={formatMessage({ id: 'storageone.mobileFirstTimeInfoToastButtonText' })}
          onButtonClick={onClose}
        />
      </KompisSheetFooter>
    </Sheet>
  ) : (
    <></>
  );
};

export default AutoFillCornerWallInfoSheet;
